import React, { useState } from 'react';
import { Link } from 'wouter';
import { Check, CircleSlash, Loader2, PencilLine, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { queryClient } from '@/lib/queryClient';

/** A change AIVIA proposed (see PendingAction in agent-actions.service.ts). Runs only on Approve. */
export interface AgentAction {
  id: string;
  type: string;
  summary: string;
  status: 'pending' | 'running' | 'done' | 'dismissed' | 'failed';
  result?: { label: string; href?: string };
  error?: string;
}

export function normalizeActions(raw: unknown): AgentAction[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a: any) => a && typeof a.id === 'string' && typeof a.summary === 'string' && ['pending', 'running', 'done', 'dismissed', 'failed'].includes(a.status),
  );
}

const inAppHref = (href?: string) => (href && /^\/(trips|clients)\/\d+$/.test(href) ? href : undefined);

function ActionCard({ action, sessionId, messageId }: { action: AgentAction; sessionId: number | null; messageId: number | null }) {
  const [current, setCurrent] = useState(action);
  const [busy, setBusy] = useState<'approve' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shown = current.status === 'pending' && action.status !== 'pending' ? action : current;

  const decide = async (decision: 'approve' | 'dismiss') => {
    if (sessionId === null || messageId === null) return;
    setBusy(decision);
    setError(null);
    try {
      const res = await fetch(`/api/threads-agent/sessions/${sessionId}/messages/${messageId}/actions/${action.id}/${decision}`, {
        method: 'POST',
        credentials: 'include',
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(res.status === 409 ? 'This was already decided.' : body?.message || 'Please try again.');
      setCurrent(body as AgentAction);
      void queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', sessionId] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const href = inAppHref(shown.result?.href);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900/40" data-action-status={shown.status}>
      <PencilLine className="h-4 w-4 shrink-0 text-orange-600 dark:text-orange-400" aria-hidden="true" />
      <span className="min-w-0 flex-1 text-gray-900 dark:text-gray-100">{shown.summary}</span>
      {shown.status === 'pending' && (
        <span className="flex shrink-0 gap-2">
          <Button size="sm" className="h-7" disabled={busy !== null || messageId === null} onClick={() => void decide('approve')}>
            {busy === 'approve' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}
            Approve
          </Button>
          <Button size="sm" variant="ghost" className="h-7" disabled={busy !== null || messageId === null} onClick={() => void decide('dismiss')}>
            Dismiss
          </Button>
        </span>
      )}
      {shown.status === 'running' && (
        <span className="inline-flex shrink-0 items-center gap-1 text-xs text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Working on it…
        </span>
      )}
      {shown.status === 'done' && (
        <span className="inline-flex shrink-0 items-center gap-1 text-xs text-green-700 dark:text-green-400">
          <Check className="h-3.5 w-3.5" /> {shown.result?.label ?? 'Done'}
          {href && (
            <Link href={href} className="ml-1 text-orange-700 underline dark:text-orange-300">
              Open
            </Link>
          )}
        </span>
      )}
      {shown.status === 'dismissed' && (
        <span className="inline-flex shrink-0 items-center gap-1 text-xs text-gray-500">
          <CircleSlash className="h-3.5 w-3.5" /> Dismissed
        </span>
      )}
      {shown.status === 'failed' && (
        <span className="inline-flex shrink-0 items-center gap-1 text-xs text-red-700 dark:text-red-400">
          <TriangleAlert className="h-3.5 w-3.5" /> Not done: {shown.error ?? 'something went wrong.'}
        </span>
      )}
      {error && <p className="w-full text-xs text-red-700 dark:text-red-400" role="alert">{error}</p>}
    </div>
  );
}

/** Changes AIVIA proposed in an answer, each with Approve / Dismiss. Nothing is saved until approved. */
export function ActionCards({ actions, sessionId, messageId }: { actions: AgentAction[]; sessionId: number | null; messageId: number | null }) {
  if (!actions.length) return null;
  return (
    <div className="mt-3 space-y-2" aria-label="Changes for your approval">
      {actions.map((a) => (
        <ActionCard key={a.id} action={a} sessionId={sessionId} messageId={messageId} />
      ))}
    </div>
  );
}
